import React, { useMemo, useState, useEffect } from 'react';
import clsx from 'clsx';
import {
    CheckCircle2, Circle, ChevronDown, ChevronLeft,
    Image as ImageIcon, FileText, Tag, Package, MapPin, CreditCard, Clock, Ticket,
    Sparkles,
} from 'lucide-react';

/**
 * Define what counts as "set up". `essential` rows drive the headline
 * progress bar; `bonus` rows are encouraged but never gate completion.
 *
 * Each row's `done(snapshot)` returns a boolean against the dashboard
 * snapshot shape — see DashboardTab for the source of truth.
 */
const hasLogo = (s) => !!(s.store?.owner_profile_url || s.store?.profile_image);
const hasCover = (s) => !!(s.store?.cover_image_url || s.store?.cover_image);
const hasAboutImage = (s) => !!(s.store?.about_image_url || s.store?.about_image);

const STEPS = [
    {
        id: 'logo',
        label: 'תמונת פרופיל (לוגו)',
        hint: 'מה שלקוחות יראו ראשון',
        icon: ImageIcon,
        targetTab: 'store-details',
        essential: true,
        done: hasLogo,
    },
    {
        id: 'about',
        label: 'תיאור קצר על העסק',
        hint: 'שתיים-שלוש שורות מי אתה',
        icon: FileText,
        targetTab: 'store-details',
        essential: true,
        done: (s) => !!(s.store?.about && s.store.about.trim().length >= 10),
    },
    {
        id: 'category',
        label: 'קטגוריה ראשונה',
        hint: 'איך לארגן את המוצרים',
        icon: Tag,
        targetTab: 'categories',
        essential: true,
        done: (s) => (s.categories?.length ?? 0) > 0,
    },
    {
        id: 'product',
        label: 'מוצר ראשון',
        hint: 'הליבה של החנות',
        icon: Package,
        targetTab: 'products',
        essential: true,
        done: (s) => (s.products?.length ?? 0) > 0,
    },
    {
        id: 'delivery',
        label: 'נקודת איסוף או אזור משלוחים',
        hint: 'איך הלקוחות יקבלו את ההזמנה',
        icon: MapPin,
        targetTab: 'delivery',
        essential: true,
        done: (s) => ((s.deliveryPoints?.length ?? 0) + (s.deliveryAreas?.length ?? 0)) > 0,
    },
    {
        id: 'payment',
        label: 'אמצעי תשלום פעיל',
        hint: 'בלי זה אי אפשר לסיים הזמנה',
        icon: CreditCard,
        targetTab: 'settings',
        essential: true,
        done: (s) => Array.isArray(s.paymentMethods) && s.paymentMethods.length > 0,
    },
    {
        id: 'cover',
        label: 'תמונת קאבר',
        hint: 'התמונה הגדולה בראש החנות',
        icon: ImageIcon,
        targetTab: 'store-details',
        essential: false,
        done: hasCover,
    },
    {
        id: 'about_image',
        label: 'תמונה לעמוד "אודות"',
        hint: 'מעניק עומק לסיפור של העסק',
        icon: ImageIcon,
        targetTab: 'store-details',
        essential: false,
        done: hasAboutImage,
    },
    {
        id: 'timeslots',
        label: 'משבצות זמן',
        hint: 'מתי לאסוף או לקבל',
        icon: Clock,
        targetTab: 'timeslots',
        essential: false,
        done: (s) => !!s.timeSlotsEnabled,
    },
    {
        id: 'coupon',
        label: 'קופון ראשון',
        hint: 'תמריץ לרכישה',
        icon: Ticket,
        targetTab: 'coupons',
        essential: false,
        done: (s) => (s.couponsCount ?? 0) > 0,
    },
];

function storageKey(storeId) {
    return `store_admin_setup_open_${storeId || 'unknown'}`;
}

// Storage stores the *expanded* state. Absent / non-'true' = collapsed.
// Collapsed-by-default keeps the dashboard calm; the ribbon stays visible
// and clickable so the checklist is never lost.
function readExpanded(storeId) {
    if (typeof window === 'undefined' || !storeId) return false;
    return localStorage.getItem(storageKey(storeId)) === 'true';
}

function writeExpanded(storeId, value) {
    if (typeof window === 'undefined' || !storeId) return;
    try { localStorage.setItem(storageKey(storeId), String(!!value)); } catch { /* ignore */ }
}

function Row({ step, done, onGo }) {
    const Icon = step.icon;
    return (
        <button
            type="button"
            onClick={() => onGo?.(step.targetTab)}
            className={clsx(
                'group w-full flex items-center gap-3 p-3 rounded-xl text-start border transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                done
                    ? 'bg-surface-muted/60 border-line-subtle'
                    : 'bg-surface border-line hover:border-brand-300 hover:bg-brand-50/40',
            )}
        >
            <span
                className={clsx(
                    'inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors',
                    done ? 'bg-success-100 text-success-600' : 'bg-surface-muted text-ink-muted group-hover:bg-brand-100 group-hover:text-brand-700',
                )}
                aria-hidden="true"
            >
                {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-[18px] h-[18px]" />}
            </span>
            <div className="flex-1 min-w-0">
                <div className={clsx('font-semibold text-sm leading-tight', done ? 'text-ink-muted line-through' : 'text-ink-strong')}>
                    {step.label}
                </div>
                <div className={clsx('text-xs mt-0.5', done ? 'text-ink-faint' : 'text-ink-subtle')}>
                    {done ? 'מוכן' : step.hint}
                </div>
            </div>
            {!done && (
                <ChevronLeft className="w-4 h-4 text-ink-faint shrink-0 transition-[color,transform] duration-200 group-hover:text-brand-600 group-hover:-translate-x-0.5" aria-hidden="true" />
            )}
        </button>
    );
}

/**
 * Persistent onboarding card. Collapses to a one-line ribbon once all
 * essentials are done (or when the owner manually collapses it). Always
 * re-expandable — newcomers (e.g. co-admins) can use it as a map.
 */
export function SetupChecklist({ snapshot, storeId, onGoToTab }) {
    const rows = useMemo(
        () => STEPS.map((s) => ({ ...s, isDone: !!s.done(snapshot || {}) })),
        [snapshot],
    );
    const essentials = rows.filter((r) => r.essential);
    const bonus = rows.filter((r) => !r.essential);
    const essentialsDone = essentials.filter((r) => r.isDone).length;
    const essentialsTotal = essentials.length;
    const allEssentialsDone = essentialsDone === essentialsTotal;
    const totalDone = rows.filter((r) => r.isDone).length;
    const totalCount = rows.length;
    const percent = Math.round((essentialsDone / Math.max(essentialsTotal, 1)) * 100);

    const [userExpanded, setUserExpanded] = useState(false);
    useEffect(() => { setUserExpanded(readExpanded(storeId)); }, [storeId]);
    useEffect(() => { writeExpanded(storeId, userExpanded); }, [storeId, userExpanded]);

    const collapsed = !userExpanded;

    if (collapsed) {
        return (
            <button
                type="button"
                onClick={() => setUserExpanded(true)}
                className={clsx(
                    'group w-full surface-card p-3.5 flex items-center gap-3 text-start',
                    'hover:border-brand-300 transition-colors animate-slide-up',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                )}
                aria-expanded="false"
                aria-label="פתיחת הגדרת חנות"
            >
                <span className={clsx(
                    'inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
                    allEssentialsDone ? 'bg-success-100 text-success-700' : 'bg-brand-100 text-brand-700',
                )}>
                    {allEssentialsDone ? <CheckCircle2 className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-ink-strong leading-tight">
                        {allEssentialsDone ? 'החנות מוכנה לקבל הזמנות' : 'הגדרת החנות'}
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        {allEssentialsDone
                            ? `${totalDone} מתוך ${totalCount} צעדים הושלמו · להרחבה`
                            : `${essentialsDone}/${essentialsTotal} צעדים הושלמו · להרחבה`}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="hidden sm:block w-28 h-1.5 rounded-pill bg-surface-muted overflow-hidden">
                        <div
                            className={clsx('h-full rounded-pill transition-[width] duration-500', allEssentialsDone ? 'bg-success-500' : 'bg-brand-500')}
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                    <ChevronDown className="w-4 h-4 text-ink-faint group-hover:text-brand-600 transition-colors" />
                </div>
            </button>
        );
    }

    return (
        <section className="surface-card p-5 animate-slide-up" aria-label="הגדרת חנות">
            <header className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3 min-w-0">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-100 text-brand-700 shrink-0">
                        <Sparkles className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                        <h3 className="heading-sm text-ink-strong leading-tight">
                            {allEssentialsDone ? 'החנות מוכנה — כל הכבוד!' : 'הצעדים הראשונים שלך'}
                        </h3>
                        <p className="text-sm text-ink-muted mt-0.5 leading-relaxed">
                            {allEssentialsDone
                                ? 'כל הבסיס מוגדר. הוסף בונוסים למטה כדי לחזק את החנות עוד יותר.'
                                : 'כמה דברים קטנים שיהפכו את החנות שלך לפעילה. אפשר בכל סדר.'}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setUserExpanded(false)}
                    aria-label="צמצום"
                    title="צמצום"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 shrink-0"
                >
                    <ChevronDown className="w-4 h-4" />
                </button>
            </header>

            <div className="mb-5">
                <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-xs font-semibold text-ink-muted">
                        {essentialsDone} מתוך {essentialsTotal} צעדים בסיסיים
                    </span>
                    <span className={clsx('text-xs font-bold tabular-nums', allEssentialsDone ? 'text-success-700' : 'text-brand-700')}>
                        {percent}%
                    </span>
                </div>
                <div className="h-2 rounded-pill bg-surface-muted overflow-hidden">
                    <div
                        className={clsx('h-full rounded-pill transition-[width] duration-500', allEssentialsDone ? 'bg-success-500' : 'bg-brand-500')}
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {essentials.map((step) => (
                    <Row key={step.id} step={step} done={step.isDone} onGo={onGoToTab} />
                ))}
            </div>

            {bonus.length > 0 && (
                <div className="mt-5 pt-4 border-t border-line-subtle">
                    <div className="text-2xs font-bold tracking-wider text-ink-faint uppercase mb-2.5">
                        בונוס · לא חובה אבל ממש משתלם
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {bonus.map((step) => (
                            <Row key={step.id} step={step} done={step.isDone} onGo={onGoToTab} />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

export default SetupChecklist;
